import { type QueryFunctionContext, useQuery, useQueryClient, useMutation, useIsMutating } from "@tanstack/react-query"

import { api } from "@/api"
import { chat } from "@/components/CopilotAnalyze/api"
import { useCopilotAuth } from "@/components/CopilotAnalyze/CopilotAuth"

import type { CopilotCustomization } from "@/components/CopilotAnalyze/types"
import type { CustomizedCopilotFormValues } from "@/components/CopilotAnalyze/CustomizedCopilot/CustomizedCopilotForm"

/***********************************************************************************************************************
 * Query Keys
 **********************************************************************************************************************/
export const copilotKeys = {
    // All - for things like bulk cache invalidation
    all: [{ scope: "copilot" }] as const,

    // Sessions
    sessions: () => [{ ...copilotKeys.all[0], entity: "sessions" }] as const,
    session: (id: string) => [{ ...copilotKeys.sessions()[0], ...(id ? { id } : {}) }] as const,

    // Sessions Mutations
    mutateSession: (sessionId?: string) => [{ ...copilotKeys.session(sessionId)[0], mutation: "sendMessage" }] as const,

    // Customizations
    copilotCustomizations: () => [{ ...copilotKeys.all[0], entity: "copilotCustomizations" }] as const,
    copilotCustomization: (id: string) =>
        [{ ...copilotKeys.copilotCustomizations()[0], ...(id ? { id } : {}) }] as const,
}

export const useCopilotSessions = () => {
    const { jwt } = useCopilotAuth()
    const isMutating = useIsMutating({ mutationKey: copilotKeys.mutateSession() })

    return useQuery({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Ignore the JWT - it is a constant
        queryKey: copilotKeys.sessions(),
        queryFn: () => chat.fetchHistory(jwt),
        // Avoid refetching while a new message has been optimistically added
        enabled: !isMutating && Boolean(jwt),
        staleTime: Infinity,
    })
}

/***********************************************************************************************************************
 * Customized Copilot - will be handled post-beta
 **********************************************************************************************************************/
const fetchCustomizedCopilots = async (): Promise<CopilotCustomization[]> => {
    try {
        const response = await api.get("/api/copilotcustomization/?archived=false")
        const responseJson = await response.json()

        return responseJson.objects
    } catch (e) {
        console.error(e)
    }
}

export const useCopilotCustomizations = () =>
    useQuery({
        queryKey: copilotKeys.copilotCustomizations(),
        queryFn: fetchCustomizedCopilots,
    })

const fetchCopilotCustomization = async ({
    queryKey: [{ id }],
}: QueryFunctionContext<ReturnType<(typeof copilotKeys)["copilotCustomization"]>>): Promise<CopilotCustomization> => {
    try {
        const response = await api.get(`/api/copilotcustomization/${id}/`)
        const responseJson = await response.json()

        return responseJson
    } catch (e) {
        console.error(e)
    }
}

export const useCopilotCustomization = (id?: string) =>
    useQuery({
        queryKey: copilotKeys.copilotCustomization(id),
        queryFn: fetchCopilotCustomization,
        enabled: Boolean(id),
        staleTime: Infinity,
        select: (data): CustomizedCopilotFormValues => {
            return {
                ...data,
                // Always render at least one issue
                organization_issues: data?.organization_issues?.length
                    ? data.organization_issues
                    : [{ description: "" }],

                // Combine Team and Just Me permissions into a single field
                permissions: data?.is_limited_to_creator ? "me" : data?.limited_to_teams?.length ? "teams" : "anyone",
                limited_to_teams:
                    data?.limited_to_teams?.map((teamUri) =>
                        DjangIO.app.userdata.models.Team.idFromResourceUri(teamUri),
                    ) || [],
            }
        },
    })

export const useMutateCopilotCustomization = (id: string) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (
            copilotCustomization: Partial<CustomizedCopilotFormValues>,
        ): Promise<CopilotCustomization> => {
            // Only include allowed_patch_fields - Would be nice to get this from DjangIO...
            const allowedPatchFields = [
                "name",
                "description",
                "organization_name",
                "organization_mission",
                "organization_issues",
                "is_limited_to_creator",
                "limited_to_teams",
                "archived",
            ] as const

            const payload = allowedPatchFields.reduce((acc, curr) => {
                if (copilotCustomization[curr] !== undefined) {
                    acc[curr] = copilotCustomization[curr]
                }
                return acc
            }, {}) as CopilotCustomization

            // Normalize permissions
            switch (copilotCustomization.permissions) {
                case "teams":
                    payload.limited_to_teams = payload.limited_to_teams.map(
                        DjangIO.app.userdata.models.Team.resourceUriFromId,
                    )
                    payload.is_limited_to_creator = false
                    break
                case "me":
                    payload.limited_to_teams = []
                    payload.is_limited_to_creator = true
                    break
                case "anyone":
                    payload.limited_to_teams = []
                    payload.is_limited_to_creator = false
                    break
                default:
            }

            try {
                const response = id
                    ? await api.patch(DjangIO.ai_assistant.models.CopilotCustomization.resourceUriFromId(id), payload)
                    : await api.post(DjangIO.ai_assistant.models.CopilotCustomization.endpoint, payload)

                return response.json()
            } catch (e) {
                console.error(e)
            }
        },
        onMutate: (copilotCustomization: CustomizedCopilotFormValues) => {
            // If a Customization was archived, we should optimistically remove it
            if (copilotCustomization.archived) {
                queryClient.setQueryData<CopilotCustomization[]>(copilotKeys.copilotCustomizations(), (prevData) =>
                    prevData.filter((customization) => String(customization.id) !== String(id)),
                )
            }
        },
        onSuccess: () => {
            // Invalidate the cache
            queryClient.invalidateQueries({ queryKey: copilotKeys.copilotCustomizations() })
        },
    })
}
